"use client";

import Link from "next/link";
import { IconSprite } from "../common";
import { ROUTES } from "@/config/routes";
import { HomeSection } from "./HomeSection"
import { cn } from "@/lib/utils"

interface MenuItem {
  id: string;
  title: string;
  shortTitle?: string;
  href: string;
  iconId: string;
}

const menuItems: MenuItem[] = [
  {
    id: "equipo-nuevo",
    title: "EQUIPO NUEVO",
    shortTitle: "NUEVO",
    href: ROUTES.industries.root,
    iconId: "ic_equipoNuevo"
  },
  {
    id: "equipo-usado",
    title: "EQUIPO USADO",
    shortTitle: "USADO",
    href: ROUTES.usedEquipments.root,
    iconId: "ic_equipoUsado"
  },
  {
    id: "equipo-renta",
    title: "EQUIPO EN RENTA",
    shortTitle: "EN RENTA",
    href: ROUTES.rentals.root,
    iconId: "ic_equipoRenta"
  },
  {
    id: "tecnologia-mineria",
    title: "TECNOLOGÍA PARA MINERÍA",
    shortTitle: "MINERÍA",
    href: ROUTES.technologySolutions.details("tecnologia-para-mineria"),
    iconId: "ic_Tecnologia"
  },
  {
    id: "tecnologia-construccion",
    title: "TECNOLOGÍA PARA CONSTRUCCIÓN",
    shortTitle: "CONTRUCCIÓN",
    href: ROUTES.technologySolutions.details("tecnologia-para-construccion"),
    iconId: "ic_Construccion"
  }
];

interface MenuItemProps {
  item: MenuItem;
  variant: 'desktop' | 'tablet' | 'mobile';
  className?: string;
}

const MenuItem = ({ item, variant, className = "" }: MenuItemProps) => {
  const getVariantStyles = () => {
    switch (variant) {
      case 'desktop':
        return {
          container: "flex flex-col items-center bg-white lg:w-[20%] hover:shadow-lg transition-all duration-300",
          iconContainer: "w-full p-2 sm:p-4 lg:p-5 flex items-center justify-center h-[180px]",
          text: "p-5 hidden sm:block text-sm sm:text-base lg:text-lg font-bold mt-2 text-center"
        };
      case 'tablet':
        return {
          container: "flex flex-col items-center bg-white md:w-[20%] h-[200px] hover:shadow-xl transition-all duration-300",
          iconContainer: "w-full p-2 sm:p-4 md:p-3 flex items-center justify-center h-[150px]",
          text: "hidden sm:block text-xs sm:text-sm md:text-base font-bold text-center leading-tight whitespace-normal"
        };
      case 'mobile':
        return {
          container: "flex flex-col items-center bg-white w-1/3 hover:shadow-xl transition-all duration-300",
          iconContainer: "w-full p-2 flex items-center justify-center h-[120px]",
          text: "p-2 block text-sm font-bold mt-2 text-center"
        };
    }
  };

  const styles = getVariantStyles();
  const displayTitle = variant === 'mobile' ? item.shortTitle : item.title;

  return (
    <Link
      href={item.href}
      className={cn(styles.container, className)}
    >
      <div className={styles.iconContainer}>
        <IconSprite
          id={item.iconId}
          className={cn("w-full h-full", item.iconId === "ic_equipoRenta" && "px-2")}
        />
      </div>
      <p className={styles.text}>
        {displayTitle}
      </p>
    </Link>
  );
};

export const SectionMenu = () => {
  const equipmentItems = menuItems.slice(0, 3);
  const technologyItems = menuItems.slice(3);

  return (
    <HomeSection
      title="SOLUCIONES EN MAQUINARIA PARA CADA RETO"
      mobileTitle="MAQUINARIA"
      viewAllLink={ROUTES.industries.root}
      className="bg-light01"
    >
      <div className="mx-auto mt-[63px]">
        {/* Versión para pantallas LG en adelante */}
        <div className="hidden lg:flex justify-center gap-4">
          {menuItems.map((item) => (
            <MenuItem key={item.id} item={item} variant="desktop" />
          ))}
        </div>

        {/* Versión para pantallas MD (solo MD, oculto en LG) */}
        <div className="hidden md:flex lg:hidden justify-center gap-4">
          {menuItems.map((item) => (
            <MenuItem key={item.id} item={item} variant="tablet" />
          ))}
        </div>

        {/* Versión para móviles/tablet: primera fila con 3 items y segunda fila con 2 items */}
        <div className="md:hidden flex flex-col gap-4">
          <p className="block text-[20px] font-bold text-center">EQUIPOS</p>
          {/* Primera fila */}
          <div className="flex justify-center gap-4">
            {equipmentItems.map((item) => (
              <MenuItem key={item.id} item={item} variant="mobile" />
            ))}
          </div>
          <p className="block text-[20px] font-bold text-center">TECNOLOGÍAS</p>
          {/* Segunda fila */}
          <div className="flex justify-center gap-4 mb-2">
            {technologyItems.map((item) => (
              <MenuItem key={item.id} item={item} variant="mobile" />
            ))}
          </div>
        </div>
      </div>
    </HomeSection>
  );
};
